import React from 'react'; // search card components
import { Loader2, ArrowRight, CheckCircle, XCircle, DollarSign, Wrench, AlertCircle, HelpCircle, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';

interface SearchCardProps {
  job: any;
  isSelecting: string | null;
  onSelectJob: (link: string, title?: string, company?: string, description?: string) => void;
}

export const SearchCard: React.FC<SearchCardProps> = ({ job, isSelecting, onSelectJob }) => {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800/80 rounded-xl p-4 hover:border-slate-300 dark:hover:border-slate-700 shadow-sm">
      <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-4">
        <div className="flex-1">
          <h4 className="text-[16px] font-bold text-slate-800 dark:text-slate-100 leading-tight mb-1">{job.title}</h4>
          <div className="flex flex-wrap items-center gap-2 text-[13px] font-medium text-slate-500 dark:text-slate-400 mt-0.5">
            <div className="flex items-center gap-1.5 shrink-0">
              {job.company}
              <a href={job.sourceLink} target="_blank" rel="noopener noreferrer" aria-label="View Source Link" className="ml-1 text-slate-400 hover:text-blue-500 inline-flex items-center justify-center p-0.5" title="View Source">
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-2 flex-1 min-w-[200px]">
              <span className={`px-2 py-0.5 text-[9px] font-bold uppercase rounded tracking-wider whitespace-nowrap ${
                job.priority === 'High' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' : 
                job.priority === 'Medium' ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400' : 
                'bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
              }`}>
                {job.priority} Priority
              </span>
              {job.companyRisk && job.companyRisk !== 'Unknown' && (
                <span className={`px-2 py-0.5 text-[9px] font-bold uppercase rounded tracking-wider whitespace-nowrap border ${
                  job.companyRisk === 'Low' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20' : 
                  job.companyRisk === 'Medium' ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20' : 
                  'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-500/10 dark:text-rose-400 dark:border-rose-500/20'
                }`}>
                  Company Risk: {job.companyRisk}
                </span>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={() => onSelectJob(job.sourceLink, job.title, job.company, job.description)}
          disabled={isSelecting === job.sourceLink}
          aria-label={`Tailor Resume for ${job.title} at ${job.company}`}
          className="flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-wider bg-slate-900 hover:bg-slate-800 text-white dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 flex-shrink-0 px-4 py-2 rounded-full font-bold disabled:opacity-50 w-full md:w-auto cursor-pointer"
        >
          Tailor Resume {isSelecting === job.sourceLink ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
        </button>
      </div>

      <div className="bg-slate-50/80 dark:bg-slate-800/30 rounded-2xl p-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-[13px] mb-3 border border-slate-200/60 dark:border-slate-700/50">
        <div className="flex items-start gap-2.5">
          <div className="flex items-center justify-center w-5 h-5 rounded shrink-0 mt-0.5">
            {job.hardConstraintResult === 'Pass' ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : 
             job.hardConstraintResult === 'Fail' ? <XCircle className="w-4 h-4 text-rose-500" /> : 
             <HelpCircle className="w-4 h-4 text-amber-500" />}
          </div>
          <div className="flex-1">
            <span className="font-semibold text-slate-700 dark:text-slate-300 block mb-0.5">Basic Fit</span>
            <p className="text-slate-500 dark:text-slate-400 leading-snug">{job.evidence}</p>

            <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 dark:border-slate-700/60">
              <span className="font-semibold flex items-center gap-1.5 text-slate-700 dark:text-slate-300 mb-1 text-[10px] uppercase tracking-wider">
                <DollarSign className="w-3 h-3 text-emerald-600 dark:text-emerald-400" /> Salary & Comp
              </span>
              <p className="text-slate-500 dark:text-slate-400 leading-snug">
                {job.salaryDetails} 
                {job.salaryRisk && !['Low', 'Unknown', 'Unclear'].includes(job.salaryRisk) && (
                  <span className="inline-flex items-center gap-0.5 opacity-90 text-amber-600 dark:text-amber-400 ml-1.5 font-medium text-[10px] bg-amber-50 dark:bg-amber-950/40 px-1 py-0.5 rounded border border-amber-200/50 dark:border-amber-900/50">
                    <AlertCircle className="w-2.5 h-2.5" /> 
                    {job.salaryRisk} Risk
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex items-start gap-2.5 mt-0.5 md:mt-0">
          <div className="flex items-center justify-center w-5 h-5 rounded shrink-0 mt-0.5">
            <Wrench className="w-4 h-4 text-slate-400" />
          </div>
          <div className="flex-1">
            <span className="font-semibold text-slate-700 dark:text-slate-300 block mb-0.5">Skills Match</span>
            {Array.isArray(job.skillLabels) && job.skillLabels.length > 0 ? (
              <div className="flex flex-wrap gap-1.5 -ml-1">
                {job.skillLabels.map((tag: string, index: number) => (
                  <Badge key={index} variant="brand" size="sm" className="scale-[0.9] origin-left border border-indigo-100 dark:border-indigo-800">
                    {tag && typeof tag === 'string' ? tag.trim() : String(tag)}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 dark:text-slate-400 leading-snug">{job.skillMatch || 'Skills fit based on your profile'}</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[12px] px-1">
        <div className="text-emerald-700 dark:text-emerald-300">
          <p className="font-bold mb-1 text-[10px] uppercase tracking-wider text-emerald-800 dark:text-emerald-400">Why Apply</p>
          {Array.isArray(job.whyApplyBullets) && job.whyApplyBullets.length > 0 ? (
            <ul className="list-disc pl-3.5 space-y-0.5 marker:text-emerald-600 dark:marker:text-emerald-500">
              {job.whyApplyBullets.map((bullet: string, i: number) => (
                <li key={i}>{bullet}</li>
              ))}
            </ul>
          ) : (
            <p>{job.whyApply || job.evidence || 'Good potential fit.'}</p>
          )}
        </div>
        
        {(Array.isArray(job.mainRiskBullets) && job.mainRiskBullets.length > 0) || job.mainRisk ? (
          <div className="text-amber-700 dark:text-amber-300">
            <p className="font-bold mb-1 text-[10px] uppercase tracking-wider text-amber-800 dark:text-amber-400">Considerations</p>
            {Array.isArray(job.mainRiskBullets) && job.mainRiskBullets.length > 0 ? (
              <ul className="list-disc pl-3.5 space-y-0.5 marker:text-amber-600 dark:marker:text-amber-500">
                {job.mainRiskBullets.map((bullet: string, i: number) => (
                  <li key={i}>{bullet}</li>
                ))}
              </ul>
            ) : (
              <p className="opacity-80">{job.mainRisk}</p>
            )}
          </div>
        ) : <div />}
      </div>
    </div>
  );
};
